import * as THREE from "three";
import {
  LANE_COLORS,
  Sparks,
  disposeGroup,
  makeBeam,
  makeSkiff,
  makeSwarmEnemy,
  type SwarmKind,
} from "./ships";

export interface Swarmling {
  kind: SwarmKind;
  group: THREE.Group;
  speed: number;
  phase: number;
  baseX: number;
  alive: boolean;
}

interface BeamFx {
  mesh: THREE.Mesh;
  age: number;
}

export class Swarm {
  readonly player: THREE.Group;
  readonly enemies: Swarmling[] = [];
  private readonly sparks: Sparks;
  private readonly beams: BeamFx[] = [];
  private readonly trailPos: Float32Array;
  private readonly trail: THREE.Points;
  private readonly enemyRoot = new THREE.Group();
  private readonly fxRoot = new THREE.Group();
  private targetX = 0;
  private targetZ = 0.6;
  private lean = 0;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    scene.add(this.enemyRoot, this.fxRoot);
    this.player = makeSkiff();
    scene.add(this.player);
    this.sparks = new Sparks(scene);

    this.trailPos = new Float32Array(60 * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.trailPos, 3));
    this.trail = new THREE.Points(
      geo,
      new THREE.PointsMaterial({ color: 0x3df0ff, size: 0.08, transparent: true, opacity: 0.7 }),
    );
    scene.add(this.trail);
  }

  get playerX(): number {
    return this.player.position.x;
  }

  setActive(active: boolean): void {
    this.player.visible = active;
    this.trail.visible = active;
    if (!active) this.reset();
  }

  setTarget(x: number, z: number): void {
    this.targetX = THREE.MathUtils.clamp(x, -9, 9);
    this.targetZ = THREE.MathUtils.clamp(z, -1.2, 2.8);
  }

  spawn(kind: SwarmKind, speed: number): void {
    const group = makeSwarmEnemy(kind);
    group.scale.setScalar(kind === "heavy" ? 1.35 : 1.2);
    const enemy: Swarmling = {
      kind,
      group,
      speed,
      phase: Math.random() * Math.PI * 2,
      baseX: (Math.random() - 0.5) * 10,
      alive: true,
    };
    group.position.set(enemy.baseX, 0.85 + (kind === "heavy" ? 0.2 : 0), -48 - Math.random() * 10);
    this.enemyRoot.add(group);
    this.enemies.push(enemy);
  }

  nearest(n: number): Swarmling[] {
    return this.enemies
      .filter((e) => e.alive)
      .sort((a, b) => b.group.position.z - a.group.position.z)
      .slice(0, n);
  }

  fireAt(targets: THREE.Vector3[], color = LANE_COLORS[0]): void {
    const from = this.player.position.clone().add(new THREE.Vector3(0, 0.2, -0.6));
    for (const to of targets) {
      const mesh = makeBeam(from, to, color);
      this.scene.add(mesh);
      this.beams.push({ mesh, age: 0 });
    }
  }

  kill(enemy: Swarmling, color?: number): void {
    if (!enemy.alive) return;
    enemy.alive = false;
    this.sparks.burst(enemy.group.position.clone(), color ?? (enemy.kind === "heavy" ? 0xff3b5c : 0xff2d95));
    this.enemyRoot.remove(enemy.group);
    disposeGroup(enemy.group);
  }

  explodeAt(pos: THREE.Vector3, color: number): void {
    this.sparks.burst(pos, color);
  }

  blink(invuln: number): void {
    this.player.visible = invuln <= 0 || Math.sin(performance.now() * 0.035) > 0;
  }

  reset(): void {
    for (const e of this.enemies) {
      this.enemyRoot.remove(e.group);
      disposeGroup(e.group);
    }
    this.enemies.length = 0;
    this.player.position.set(0, 0.55, 0.6);
    this.player.rotation.set(0, 0, 0);
    this.player.visible = true;
    this.targetX = 0;
    this.targetZ = 0.6;
  }

  update(dt: number): { collisions: Swarmling[] } {
    this.player.position.x = THREE.MathUtils.damp(this.player.position.x, this.targetX, 8, dt);
    this.player.position.z = THREE.MathUtils.damp(this.player.position.z, this.targetZ, 7, dt);
    const dx = this.targetX - this.player.position.x;
    this.lean = THREE.MathUtils.damp(this.lean, -dx * 0.18, 10, dt);
    this.player.rotation.z = this.lean;
    this.player.rotation.y = dx * 0.04;

    for (let i = this.trailPos.length - 3; i >= 3; i -= 3) {
      this.trailPos[i] = this.trailPos[i - 3]!;
      this.trailPos[i + 1] = this.trailPos[i - 2]!;
      this.trailPos[i + 2] = this.trailPos[i - 1]!;
    }
    this.trailPos[0] = this.player.position.x;
    this.trailPos[1] = this.player.position.y;
    this.trailPos[2] = this.player.position.z + 0.7;
    (this.trail.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;

    const collisions: Swarmling[] = [];
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      enemy.group.position.z += enemy.speed * dt;
      enemy.group.position.x =
        enemy.baseX +
        Math.sin(performance.now() * 0.001 * (1.4 + enemy.speed * 0.05) + enemy.phase) *
          (enemy.kind === "striker" ? 2.4 : 1.4);
      enemy.group.rotation.y += dt * (enemy.kind === "striker" ? 4 : 1.6);
      enemy.group.rotation.x += dt * 0.7;
      const dz = enemy.group.position.z - this.player.position.z;
      const dxe = enemy.group.position.x - this.player.position.x;
      if (Math.hypot(dxe, dz) < 0.95) collisions.push(enemy);
      else if (enemy.group.position.z > this.player.position.z + 2.2) this.despawn(enemy);
    }
    this.enemies.splice(0, this.enemies.length, ...this.enemies.filter((e) => e.alive));

    for (const beam of this.beams) {
      beam.age += dt;
      const mat = beam.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, 1 - beam.age / 0.12);
    }
    this.beams.splice(
      0,
      this.beams.length,
      ...this.beams.filter((b) => {
        if (b.age < 0.12) return true;
        this.scene.remove(b.mesh);
        b.mesh.geometry.dispose();
        (b.mesh.material as THREE.Material).dispose();
        return false;
      }),
    );

    this.sparks.update(dt);
    return { collisions };
  }

  private despawn(enemy: Swarmling): void {
    if (!enemy.alive) return;
    enemy.alive = false;
    this.enemyRoot.remove(enemy.group);
    disposeGroup(enemy.group);
  }
}
